import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import OtpVerification from '../components/OtpVerification';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpPurpose, setOtpPurpose] = useState('');

  const routeAfterAuth = (user) => {
    if (user.user_type === 'admin') navigate('/admin-dashboard');
    else if (user.user_type === 'organization') navigate('/organization-dashboard');
    else navigate('/candidate-dashboard');
  };

  const handleOtpVerified = (payload) => {
    localStorage.setItem('user', JSON.stringify(payload.user));
    localStorage.setItem('token', payload.token);
    routeAfterAuth(payload.user);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      // OTP flow: signup verification OR new-device verification
      if (response.data.status === 'otp_required') {
        setOtpEmail(response.data.email);
        setOtpPurpose(response.data.purpose || 'login');
        return;
      }
      if (response.data.status === 'success') {
        setSuccess('Login successful! Redirecting...');
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('token', response.data.token);
        setTimeout(() => routeAfterAuth(response.data.user), 1000);
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (otpEmail) {
    return (
      <div data-testid="login-otp-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          <OtpVerification
            email={otpEmail}
            purpose={otpPurpose}
            onVerified={handleOtpVerified}
            onCancel={() => { setOtpEmail(''); setOtpPurpose(''); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="login-page" className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md text-center">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-meritum-teal hover:text-meritum-navy font-body text-sm mb-6 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          
          {/* Logo */}
          <div className="mb-12">
            <Link to="/" className="inline-block">
              <img
                src={LOGO_URL}
                alt="MeritumPraxis"
                className="h-24 w-auto object-contain mx-auto drop-shadow-lg"
              />
            </Link>
          </div>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-meritum-navy mb-3 text-left">
            Welcome Back
          </h1>
          <p className="font-body text-slate-600 mb-8 text-left">
            Sign in to access your MeritumPraxis account
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body text-sm font-medium text-meritum-navy">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="border-slate-300 focus:border-meritum-teal py-6"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="font-body text-sm font-medium text-meritum-navy">
                  Password
                </Label>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="border-slate-300 focus:border-meritum-teal py-6 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="login-error">
                <p className="font-body text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm" data-testid="login-success">
                <p className="font-body text-sm text-green-600">{success}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-meritum-teal text-white hover:bg-meritum-navy font-body font-bold py-6 text-base"
              data-testid="login-submit-btn"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </form>

          <p className="font-body text-center text-slate-600 mt-8">
            Don't have an account?{' '}
            <Link
              to="/pricing"
              className="text-meritum-gold font-semibold hover:text-meritum-teal transition-colors"
              data-testid="signup-link"
            >
              Create Profile
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal items-center justify-center p-12">
        <div className="text-center text-white max-w-md">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Progress Made Visible
          </h2>
          <p className="font-body text-lg text-slate-200 leading-relaxed mb-8">
            Access your verified professional profile and continue building your career 
            with evidence-based achievements.
          </p>
          <div className="grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="font-body text-sm text-meritum-gold font-semibold">Candidates</p>
              <p className="font-body text-xs text-slate-300 mt-1">Build your profile</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="font-body text-sm text-meritum-gold font-semibold">Organizations</p>
              <p className="font-body text-xs text-slate-300 mt-1">Discover skill</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
